<template>
    <div class="tw:flex tw:gap-2 tw:items-start">
        <!-- Input de número de documento -->
        <BaseInputText
            :value="props.value"
            :label="inputLabel"
            :name="fieldName"
            :rules="props.rules || computedRules"
            :disabled="props.disabled || !props.documentType"
            :maxlength="maxlengthForType"
            :api-validation-error="props.apiValidationError"
            :data-cy="props.dataCy"
            :class="props.documentType === 'PASAPORTE' ? 'tw:w-2/3' : 'tw:w-full'"
            @input="val => emit('input', val)"
            @change="val => emit('change', val)"
        />

        <!-- Selector de país SÓLO cuando el tipo es PASAPORTE -->
        <BaseMultiselect
            v-if="props.documentType === 'PASAPORTE'"
            :value="props.paisId"
            :options="paises"
            value-key="id"
            label-key="nombre"
            label="País"
            name="pais_pasaporte"
            :disabled="props.disabled"
            :loading="loadingPaises"
            custom-class="document-input-pais tw:min-w-[180px]"
            @input="val => emit('update:paisId', val)"
        >
            <template slot="singleLabel" slot-scope="{ option }">
                <span class="tw:flex tw:items-center tw:gap-1.5">
                    <img
                        v-if="option.iso"
                        :src="getFlagUrl(option.iso)"
                        :alt="option.nombre"
                        class="tw:h-4 tw:w-6 tw:object-cover tw:border tw:border-slate-200 tw:rounded-sm"
                        @error="onFlagError($event)"
                    />
                    <b class="tw:text-xs">{{ option.iso3 || option.iso || option.nombre }}</b>
                </span>
            </template>
            <template slot="option" slot-scope="{ option }">
                <span class="tw:flex tw:items-center tw:gap-2">
                    <img
                        v-if="option.iso"
                        :src="getFlagUrl(option.iso)"
                        :alt="option.nombre"
                        class="tw:h-5 tw:w-7 tw:object-cover tw:border tw:border-slate-200 tw:rounded-sm"
                        @error="onFlagError($event)"
                    />
                    <span class="tw:font-medium">{{ option.iso3 || option.iso || '---' }}</span>
                </span>
            </template>
        </BaseMultiselect>
    </div>
</template>

<script setup>
    import { ref, computed, watch, onMounted } from 'vue';
    import BaseInputText from '@/modules/admin/ui/BaseInputText.vue';
    import BaseMultiselect from '@/modules/admin/ui/BaseMultiselect.vue';

    /**
     * DocumentInput — Input de número de documento para admin.
     *
     * Wrapper sobre BaseInputText que ajusta el maxlength según
     * el tipo de documento. Si el tipo es PASAPORTE, muestra
     * además un selector de país al lado.
     *
     * @prop {String}  value               — Valor del documento (v-model via @input).
     * @prop {String}  [documentType]      — Tipo de documento ('DNI'|'NIE'|'NIF'|'PASAPORTE'|'OTROS').
     * @prop {Number}  [paisId]            — ID del país (solo relevante para PASAPORTE).
     * @prop {String}  [label]             — Label del input.
     * @prop {String}  [name]              — Nombre del campo para VeeValidate.
     * @prop {String|Object} [rules]       — Reglas de validación VeeValidate.
     * @prop {Boolean} [disabled]          — Deshabilitar.
     * @prop {String|Array} [apiValidationError] — Error del servidor.
     * @prop {String}        [dataCy]             — Selector E2E (Playwright data-cy) sobre el input.
     *
     * @emits input         — Nuevo valor del documento.
     * @emits change        — Valor al hacer blur.
     * @emits update:paisId — Nuevo país seleccionado (solo PASAPORTE).
     */
    const props = defineProps({
        value: [String, Number],
        documentType: String,
        paisId: Number,
        label: String,
        name: String,
        rules: [String, Object],
        disabled: Boolean,
        apiValidationError: [String, Array],
        dataCy: String,
    });

    const emit = defineEmits(['input', 'change', 'update:paisId']);

    // ── Helpers para banderas (flagcdn) ──
    const getFlagUrl = codigo => {
        if (!codigo) return '';
        return `https://flagcdn.com/w40/${codigo.toLowerCase()}.png`;
    };
    const onFlagError = e => {
        e.target.style.display = 'none';
    };

    // ── Reglas de validación dinámicas según tipo de documento ──
    //   Emula la lógica de getValidationRules() en DocumentoValor.vue legacy.
    const computedRules = computed(() => {
        if (!props.documentType) return '';

        const rules = {
            required: true,
        };

        switch (props.documentType) {
            case 'DNI':
            case 'NIE':
                rules.identificacion = { tipo: props.documentType };
                break;
            case 'NIF':
                rules.valid_nif_cif = true;
                break;
            case 'PASAPORTE': {
                // Necesitamos el ISO del país para la regla 'pasaporte'
                const pais = paises.value.find(p => p.id === props.paisId);
                rules.pasaporte = { pais: pais?.iso || '' };
                break;
            }
            case 'OTROS':
                rules.identificacion = { tipo: 'otros' };
                break;
            // default: solo required
        }

        return rules;
    });

    // ── Maxlength por tipo de documento ──
    const MAXLENGTH_BY_TYPE = {
        DNI: 9,
        NIF: 9,
        NIE: 9,
        PASAPORTE: 20,
    };
    const DEFAULT_MAXLENGTH = 30;

    const maxlengthForType = computed(() => {
        if (!props.documentType) return DEFAULT_MAXLENGTH;
        const key = props.documentType.toUpperCase();
        return MAXLENGTH_BY_TYPE[key] ?? DEFAULT_MAXLENGTH;
    });

    // ── Label dinámico según tipo de documento ──
    const inputLabel = computed(() => {
        if (props.label) return props.label;

        switch (props.documentType) {
            case 'DNI': return 'DNI *';
            case 'NIE': return 'NIE *';
            case 'NIF': return 'NIF *';
            case 'PASAPORTE': return 'PASAPORTE *';
            default: return 'Documento *';
        }
    });

    // Nombre para VeeValidate — dinámico según tipo de documento
    const fieldName = computed(() => {
        if (props.name) return props.name;
        return inputLabel.value.replace(/\s?\*$/, '');
    });

    // ── Carga de países para PASAPORTE (mismo endpoint que el legacy) ──
    import { useGetPaises } from '@/composables/common/useGetPaises';

    const { paises, loadingPaises, cargarPaises } = useGetPaises();

    watch(() => props.documentType, newType => {
        if (newType === 'PASAPORTE') {
            cargarPaises();
        }
    });

    onMounted(() => {
        if (props.documentType === 'PASAPORTE') {
            cargarPaises();
        }
    });
</script>

<script>
    export default {
        inheritAttrs: false,
    };
</script>

<style scoped>
/* Evita scroll horizontal en el dropdown de países PASAPORTE */
.document-input-pais :deep(.multiselect__content) {
    display: block !important;
    width: 100%;
}
</style>
